<template>
        <div class="section3">
            <div class="section bordered">
                <h2>Institutional Background</h2>


                <b-row class="mt-3">
                    <b-col lg="6">
                        <p>
                            Please provide your institutional information below.
                        </p>
                    </b-col>
                </b-row>
                <div class="content" data-section-name="institutionalinfo">
                    <b-row >
                        <b-col lg="6">
                            <label class="thick">Home institution name (unabbreviated)<span
                                    class="arm-required" title="Please fill out this field."> *</span>
                            </label>
                            <p v-if="!allowFreeText">
                                Please search for and pick your home institution. If your institution is not listed, you may check the box and enter your institution name.
                            </p>
                            <p v-else>
                                Please enter your institution name in the text box.
                            </p>
                        </b-col>
                        
                        <b-col lg="6">
                        <div class="position-relative d-inline-block w-100">
                            <VueAutosuggest v-model="affiliation.name" v-if="!allowFreeText" :suggestions="suggestions"
                                :inputProps="inputProps" :getSuggestionValue="getSuggestionValue"
                                :disabled="allowFreeText" @input="fetchAffiliationOptions" @selected="onSelected">
                                <template slot-scope="{suggestion}">
                                    <span class="my-suggestion-item">
                                        {{ suggestion.item.name }}, {{ suggestion.item.country }}
                                    </span>
                                </template>
                            </VueAutosuggest>
                            <b-icon v-if="!allowFreeText && affiliation.name && !people.rorId" icon="x"
                                variant="danger" class="position-absolute"
                                style="right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none;" />
                            
                            <b-icon v-if="!allowFreeText && affiliation.name && people.rorId" icon="check"
                                variant="success" class="position-absolute"
                                style="right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none;" />
                            
                        </div>
                            <!-- Show free text input when checkbox is checked -->
                            <b-form-input
                                v-if="allowFreeText"
                                v-model="people.affiliation"
                                placeholder="Enter your institution name..."
                                class="mt-2 .checkbox-aligned"
                                :state="validateState('affiliation')"
                            />

                            <!-- Add checkbox for unlisted institutions -->
                            <div
                                class="checkbox-aligned">
                                <b-form-checkbox
                                    v-model="allowFreeText"
                                    class="mt-2"
                                    @change="onFreeTextToggle"
                                />
                                <p
                                    class="mt-2">
                                    My institution is not listed
                                </p>
                            </div>
                            <b-form-invalid-feedback
                              :state="validateState('affiliation')"
                              id="feedback"
                              class="thick"
                            >
                            <span v-if="this.allowFreeText">
                                Institution name is required and must not include invalid characters such as
                              {{ restrictedInstitutionsCharacters }}
                            </span>
                            <span v-else>
                                Please select an institution from the list or check the box to enter it manually.
                            </span>

                            </b-form-invalid-feedback>

                            <div
                                v-if="people.rorId"
                                class="institution-info p-2 mt-2"
                            >
                                ROR ID: {{ affiliation.id }}
                                <br />
                                Country: {{ affiliation.country }}
                            </div>
                        </b-col>
                    </b-row>


                    <!-- Address Section -->
                    <b-row class="mt-3">
                        <b-col lg="6">
                            <label class="thick">Street <span class="arm-required">*</span></label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-input 
                                v-model.trim="people.address1" 
                                class="rounded-0" 
                                placeholder="Street ..." 
                                :state="validateState('address1')" 
                                aria-describedby="feedback"
                            />
                            <b-form-invalid-feedback id="feedback" class="thick">
                                Street is required and must not include invalid characters such as {{ restrictedCharacters }}
                            </b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                    
                    <b-row class="mt-3">
                        <b-col lg="6">
                            <label>Street 2</label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-input
                                v-model.trim="people.address2"
                                class="rounded-0"
                                placeholder="Street 2 ..."
                                :state="validateState('address2')"
                                aria-describedby="feedback"
                            />
                            <b-form-invalid-feedback id="feedback" class="thick">
                                Street 2 must not include invalid characters such as {{ restrictedCharacters }}
                            </b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                    
                    <b-row class="mt-3">
                        <b-col lg="6">
                            <label class="thick">City <span class="arm-required">*</span></label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-input 
                                v-model.trim="people.city" 
                                class="rounded-0" 
                                placeholder="City ..." 
                                :state="validateState('city')" 
                                aria-describedby="feedback"
                            />
                            <b-form-invalid-feedback id="feedback" class="thick">
                                City is required and must not include invalid characters such as {{ restrictedCharacters }}
                            </b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                    
                    <b-row class="mt-3">
                        <b-col lg="6">
                           <label class="thick">State<span class="arm-required">*</span></label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-select 
                                v-model.trim="people.state" 
                                class="rounded-0" 
                                :options="states" 
                                :state="people.countryCode !== 'US' ? null : validateState('state')"
                                aria-describedby="feedback"
                                :disabled="people.countryCode !== 'US'"
                            />
                            <b-form-invalid-feedback 
                                v-if="people.countryCode === 'US'"
                                id="feedback" 
                                class="thick">
                                State is required and must not include invalid characters such as {{ restrictedCharacters }}
                            </b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                    
                    <b-row class="mt-3">
                        <b-col lg="6">
                            <label class="thick">Postal code <span class="arm-required">*</span></label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-input
                                v-model.trim="people.zipCode" 
                                class="rounded-0"
                                placeholder="Postal code ..."
                                :state="validateState('zipCode')"
                                aria-describedby="feedback"
                            />
                            <b-form-invalid-feedback id="feedback" class="thick" v-if="!$v.people.zipCode.required">
                                Postal code is required
                            </b-form-invalid-feedback>
                            <b-form-invalid-feedback id="feedback" class="thick" v-if="!$v.people.zipCode.maxLength">
                                Postal code can have at most {{$v.people.zipCode.$params.maxLength.max}} digits
                            </b-form-invalid-feedback>
                            <b-form-invalid-feedback id="feedback" class="thick">
                                Postal code must not include invalid characters such as {{ restrictedCharacters }}
                            </b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                    
                    <b-row class="mt-3">
                        <b-col lg="6">
                            <label class="thick">Country <span class="arm-required">*</span></label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-select
                                v-model.trim="people.countryCode"
                                class="rounded-0"
                                :options="countries"
                                :state="validateState('countryCode')"
                                aria-describedby="feedback"
                                @change="validateStateCode"
                            />
                            <b-form-invalid-feedback id="feedback" class="thick">
                                Country is required
                            </b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                    <b-row class="mt-4">
                        <b-col lg="6">
                            <label class="thick">Is your home institution a small business?<span
                                    class="arm-required" title="Please fill out this field."> *</span>
                            </label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-radio-group v-model="people.isSmallBusiness" class="arm-radios" :options="businessOptions" :state="validateState('isSmallBusiness')" stacked>
                                <b-form-invalid-feedback :state="validateState('isSmallBusiness')" class="thick">Please select yes or no</b-form-invalid-feedback>
                            </b-form-radio-group>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col lg="6">
                            <label class="thick">Home institution employment position<span
                                    class="arm-required" title="Please fill out this field."> *</span>
                            </label>
                            <p>
                                Office of Science is interested in understanding the general career stage and positions of facility users.  Please select the category that best matches your position/career stage, even if it is not your exact title.
                            </p>
                        </b-col>
                        <b-col lg="6">
                            <b-form-select v-model="people.employmentTypeCode" class="rounded-0" :options="empTypeOptions" id="employmentTypeCode" :state="validateState('employmentTypeCode')" aria-describedby="feedback" @change="empTypeChange"></b-form-select>
                            <b-form-invalid-feedback id="feedback" class="thick">Employment type is required</b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col lg="6">
                            <label class="thick">
                                How did you hear about the ARM user facility or ARM data?
                                <span
                                    class="arm-required"
                                    title="Please fill out this field."
                                >
                                    *
                                </span>
                            </label>
                        </b-col>
                        <b-col lg="6">
                            <b-form-select
                                v-model="people.surveyResults"
                                class="rounded-0"
                                :options="surveyOptions"
                                :state="validateState('surveyResults')"
                                aria-describedby="feedback"
                            />
                            <b-form-invalid-feedback
                                id="feedback"
                                class="thick"
                            >
                                This field is required
                            </b-form-invalid-feedback>
                        </b-col>
                    </b-row>
                </div>
            </div>
            <b-modal id="empConfirmModal" title="Please Confirm" ok-title="Confirm" no-fade ok-only hide-header-close no-close-on-backdrop centered @ok="confirmedEmpPositionModal">
                <p>
                    You have selected "Other". Please confirm that none of the above employment position categories are appropriate. Note that Office of Science is most interested in understanding the general career stage/employment position of users, not in specific position titles.
                </p>
            </b-modal>
        </div>
</template>

<script>
    import { mapState } from "vuex"
    import { validationMixin } from "vuelidate";
    import { required, requiredIf, maxLength } from "vuelidate/lib/validators";
    import { VueAutosuggest } from 'vue-autosuggest';
    import Config from "@/config"

    const stateValidator = Config.validations.stateValidator
    const oneOrMoreValidator = Config.validations.oneOrMoreValidator
    const zeroOrMoreValidator = Config.validations.zeroOrMoreValidator
    const institutionsValidator = Config.validations.institutionsValidator

    export default {
        name: "InstitutionalTemplate",
        mixins: [validationMixin],
        props: {
            people: {default: null, type: Object},
            isRecordSubmitted: {default: false, type: Boolean}
        },
        components: {
            VueAutosuggest
        },
        data() {
            return {
                affiliation: {
                    id: null,
                    name: null,
                    country: null,
                },
                allowFreeText: false,
                surveyOptions: [
                    {text:"Select one ...", value:null},
                    {text:"Colleague", value:"Colleague"},
                    {text:"Professor", value:"Professor"},
                    {text:"Search Engine", value:"Search Engine"},
                    {text:"Social Media", value:"Social Media"},
                    {text:"Journal Article or News Article", value:"Journal Article or News Article"},
                    {text:"Request for Field Campaign Announcement (EOS, BAMS)", value:"Request for Field Campaign Announcement (EOS, BAMS)"},
                    {text:"Conference or Meeting (e.g., AGU, AMS, EGU)", value:"Conference or Meeting"},
                ],
                businessOptions: [
                    { text: "Yes", value: true},
                    { text: "No", value: false}
                ],
                suggestions: [],
            }
        },
        validations: {
            people: {
                affiliation: {
                    required: requiredIf(function() {
                        return this.people?.rorId ? false : true
                    }),
                    institutionsValidator,
                },
                address1: {
                    required,
                    oneOrMoreValidator,
                },
                address2: {
                    zeroOrMoreValidator,
                },
                city: {
                    required,
                    oneOrMoreValidator,
                },
                state: {
                    required: requiredIf(function() {
                        return this.people?.countryCode === "US" ? true : false
                    }),
                    stateValidator,
                },
                zipCode: {
                    required,
                    maxLength: maxLength(10),
                    oneOrMoreValidator,
                },
                countryCode: {
                    required,
                    oneOrMoreValidator,
                },
                rorId: {
                    required: requiredIf(function() {
                        // return this.people?.affiliation ? false : true
                        return !this.allowFreeText
                    }),
                    institutionsValidator,
                },
                isSmallBusiness: {
                    required,
                },
                employmentTypeCode: {
                    required,
                    oneOrMoreValidator,
                },
                surveyResults: {
                    required,
                    oneOrMoreValidator,
                }
            }
        },
        computed: {
            ...mapState("app",["instOptions", "empTypeOptions","countries","states"]),
            restrictedCharacters() {
              return Config.restrictedCharacters
            },
            restrictedInstitutionsCharacters() {
              return Config.restrictedInstitutionsCharacters
            },
            inputProps() {
                return {
                    id: "autosuggest__input",
                    placeholder: "Home Institution Name...",
                    class: "form-control", // Provides Bootstrap Styling to input
                    disabled: this.allowFreeText
                }
            },
            validationObject() {
              return JSON.stringify(this.$v.people)
            },
        },
        created() {
            this.$store.dispatch("app/getEmpTypeOptions")
            this.$store.dispatch("app/getStates")
            this.$store.dispatch("app/getCountries")
        },
        watch: {
            isRecordSubmitted() {
                this.$v.people.$touch();
            },
            validationObject: {
              handler(newVal, oldVal) {
                const a = JSON.parse(newVal)
                const b = JSON.parse(oldVal)
                Object.keys(a.$params).forEach(key => {
                  if (a[key].$model !== b[key].$model) {
                    this.$v.people[key].$touch()
                    this.$emit("institutionValid", !this.$v.people.$invalid)
                  }
                })
              },
              deep: true,
            },
            "people.affiliation": {
                handler(newVal, oldVal) {
                    if (newVal && !oldVal) {
                        this.fetchAffiliationInfo()
                    }
                },
                deep: true,
            },
            "people.rorId": {
                handler(newVal, oldVal) {
                    if (newVal && !oldVal) {
                        this.fetchAffiliationInfo()
                    }
                },
                deep: true,
            },
            "people.countryCode"(newVal){
                if (newVal !== "US") {
                    this.people.state = null
                }
            }
        },
        methods: {
            fetchAffiliationInfo() {
                if (this.people?.rorId) {
                    const prefix = Config.identifiers.ror_id_prefix
                    const rorId = this.people.rorId
                    const uri = prefix + rorId
                    this.$store.dispatch("app/fetchAffiliationInfo", uri).then(response => {
                        if (response?.[0]?.name) {
                            this.affiliation = response?.map(datum => {
                                return {
                                    id: rorId,
                                    name: datum?.name,
                                    country: datum?.country?.country_name,
                                }
                            })[0]
                        }
                    })
                } else if (this.people?.affiliation) {
                    this.affiliation = {
                        id: this.people.affiliation,
                        name: this.people.affiliation,
                    }
                }
            },
            onFreeTextToggle(checked) {
                if (checked) {
                    this.people.rorId = null
                    this.people.affiliation = null
                    this.affiliation = {
                        id: null,
                        name: null,
                        country: null,
                    }
                } else {
                    this.people.affiliation = null
                    this.affiliation.name = null
                }

                this.$nextTick(() => {
                    this.$emit("institutionValid", !this.$v.people.$invalid)
                })
            },
            fetchAffiliationOptions(filter) {
                if (filter && !this.allowFreeText) {
                    // Clear ROR data when typing (before selection)
                    this.people.rorId = null
                    this.people.affiliation = null
                    this.affiliation.country = null

                    this.$store.dispatch("app/searchAffiliationOptions", filter.toLowerCase()).then(response => {
                        this.suggestions = [{
                            data: response?.map(datum => {
                                const prefix = Config.identifiers.ror_id_prefix
                                const uri = datum?.id
                                const id = uri.replace(prefix, "")
                                return {
                                    id,
                                    name: datum?.name,
                                    country: datum?.country?.country_name,
                                }
                            })
                        }]
                    })
                } else if (!filter) {
                    this.affiliation = {
                        id: null,
                        name: null,
                        country: null,
                    }
                    this.people.affiliation = null
                    this.people.rorId = null
                }
            },
            confirmedEmpPositionModal() {
                this.people.employmentTypeConfirm = true
            },
            empTypeChange() {
                // Display confirmation modal only when 'Other' is selected and haven't confirmed it before
                if (this.people.employmentTypeCode === "X" && !this.people.employmentTypeConfirm) {
                    this.$bvModal.show("empConfirmModal")
                } else {
                    this.people.employmentTypeConfirm = null
                }
            },
            validateState(name) {
                const {$dirty, $error} = this.$v.people[name];
                return $dirty ? !$error : null;
            },
            validateStateCode (val) {
                this.people.state = val && val != "US" ? null : this.people.state
            },
            validateAffiliation() {
                return this.people.affiliation ? true : false
            },
            getSuggestionValue(suggestion) {
                return suggestion.item
            },
            fetchResults(searchText) {
                var result = this.instOptions.filter(val => {
                    return val.toLowerCase().startsWith(searchText.toLowerCase())
                })
                this.suggestions = [{
                    data: result
                }]
            },
            onSelected(text) {
                this.affiliation = text.item
                this.people.affiliation = text.item.name
                this.people.rorId = text.item.id
            }
        }
    }
</script>

<style>
.is-invalid .input-group input {
    border-color: #dc3545;
}

.institution-info {
    color: white;
    background-color: #5180A8;
    font-weight: bold;
    border-radius: 4px;
}

.checkbox-aligned {
    display: flex;
    align-items: left;
}
.checkbox-aligned p {
    padding-top: 0.25rem;
}
</style>
